import { useCallback } from 'react';
import { isCancelledError, useQuery, useQueryClient } from '@tanstack/react-query';
import { FetchDashboardSummary } from '../../api/dashboard/FetchDashboardSummary';
import { FetchDashboardAttention } from '../../api/dashboard/FetchDashboardAttention';
import { FetchDashboardTrends } from '../../api/dashboard/FetchDashboardTrends';
import { FetchDashboardInventory } from '../../api/dashboard/FetchDashboardInventory';
import { FetchDashboardRecent } from '../../api/dashboard/FetchDashboardRecent';
import { FetchDashboardSystem } from '../../api/dashboard/FetchDashboardSystem';
import { DASHBOARD_SECTIONS } from '../../constants/dashboardSections';

export const DASHBOARD_QUERY_KEY = 'dashboard';

// The server keeps one shared snapshot that only the Refresh button recomputes.
// On mount the widget paints its saved copy (persisted across reloads) and quietly
// re-reads the server snapshot, which never recomputes. No polling, no refocus.
const queryOptions = (enabled = true) => ({
  enabled,
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchOnMount: 'always',
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  retry: 1,
});

const widgets = {
  summary: () => ({
    label: DASHBOARD_SECTIONS.summary,
    queryKey: [DASHBOARD_QUERY_KEY, 'summary'],
    fetch: FetchDashboardSummary,
  }),
  attention: () => ({
    label: DASHBOARD_SECTIONS.attention,
    queryKey: [DASHBOARD_QUERY_KEY, 'attention'],
    fetch: FetchDashboardAttention,
  }),
  trends: ({ days }) => ({
    label: DASHBOARD_SECTIONS.trends,
    queryKey: [DASHBOARD_QUERY_KEY, 'trends', days],
    fetch: (params) => FetchDashboardTrends({ days, ...params }),
  }),
  inventory: () => ({
    label: DASHBOARD_SECTIONS.inventory,
    queryKey: [DASHBOARD_QUERY_KEY, 'inventory'],
    fetch: FetchDashboardInventory,
  }),
  recent: () => ({
    label: DASHBOARD_SECTIONS.recent,
    queryKey: [DASHBOARD_QUERY_KEY, 'recent'],
    fetch: FetchDashboardRecent,
  }),
  system: () => ({
    label: DASHBOARD_SECTIONS.system,
    queryKey: [DASHBOARD_QUERY_KEY, 'system'],
    fetch: FetchDashboardSystem,
  }),
};

// Every widget owns its query, so one failing endpoint never blanks the page.
const useWidget = (widget, enabled) =>
  useQuery({
    queryKey: widget.queryKey,
    queryFn: () => widget.fetch(),
    ...queryOptions(enabled),
  });

export const useDashboardSummary = () => useWidget(widgets.summary());
export const useDashboardAttention = () => useWidget(widgets.attention());
export const useDashboardTrends = ({ days }) => useWidget(widgets.trends({ days }));
export const useDashboardInventory = () => useWidget(widgets.inventory());
export const useDashboardRecent = () => useWidget(widgets.recent());
export const useDashboardSystem = ({ enabled }) => useWidget(widgets.system(), enabled);

// Asks the server to recompute each section (refresh=true) and overwrite its cached
// snapshot. Never rejects: reports which sections refreshed, which failed, and whether
// a failed one still has earlier data on screen. A section whose request was cancelled
// by a newer refresh is left out of both lists.
export const refreshSections = async (queryClient, sections) => {
  // allSettled: a failing section shows its own state without stopping the others.
  const results = await Promise.allSettled(
    sections.map(async (section) => {
      // An in-flight quiet sync would otherwise be reused and refresh=true never sent.
      await queryClient.cancelQueries({ queryKey: section.queryKey, exact: true });

      return queryClient.fetchQuery({
        queryKey: section.queryKey,
        queryFn: () => section.fetch({ refresh: true }),
        // fetchQuery otherwise inherits the app-wide 5 min staleTime and silently
        // returns the cached data without making a request.
        staleTime: 0,
        retry: 1,
      });
    })
  );

  const refreshed = [];
  const failed = [];

  results.forEach((result, index) => {
    const section = sections[index];

    if (result.status === 'fulfilled') {
      refreshed.push(section.label);
    } else if (!isCancelledError(result.reason)) {
      failed.push({
        label: section.label,
        hasData: queryClient.getQueryData(section.queryKey) !== undefined,
      });
    }
  });

  return { refreshed, failed };
};

export const useDashboardRefresh = ({ days, includeSystem }) => {
  const queryClient = useQueryClient();

  // The Refresh button: every section on screen.
  const refreshAll = useCallback(
    () =>
      refreshSections(queryClient, [
        widgets.summary(),
        widgets.attention(),
        widgets.trends({ days }),
        widgets.inventory(),
        widgets.recent(),
        ...(includeSystem ? [widgets.system()] : []),
      ]),
    [queryClient, days, includeSystem]
  );

  // A card's Retry after its refresh failed: just that section.
  const refreshSection = useCallback(
    (name) => refreshSections(queryClient, [widgets[name]({ days })]),
    [queryClient, days]
  );

  return { refreshAll, refreshSection };
};
